import uniqBy from "lodash/uniqBy";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DATE_FORMATS } from "../../constants";
import { isAfterToday } from "../../utils/date-helpers";
import { buildLogRowProps, buildPlanRowProps } from "./weekPlanRowProps";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

const fetchJson = (url) =>
  fetch(url, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  }).then((response) => response.json().then((data) => ({ response, data })));

// Cycle docs for Plan's training card (real program/block/workout names,
// not just the exercise's cycle reference) - genuinely can't run in
// parallel with the primary fetch, since the ids to ask for only exist once
// the primary calendar-plan response (data.exercises) is in hand.
const loadCycles = async (data) => {
  const cycleIds = uniqBy(
    (data.exercises || []).filter((e) => e.cycle),
    (e) => String(e.cycle.id)
  ).map((e) => e.cycle.id);
  if (!cycleIds.length) return [];

  const { response, data: cycles } = await fetchJson(
    `/actions/annual-planner/json/week-cycles?ids=${cycleIds.join(",")}`
  );
  return response.ok ? cycles || [] : [];
};

// PR flags for Log's training card - unlike cycles above, this only needs
// the athlete/date range (already known before the primary fetch even
// starts), so the caller runs it in parallel with calendar-log instead of
// waiting on that response first.
const loadDayPersonalRecords = async (athleteId, startDate, endDate) => {
  if (isAfterToday(endDate)) return {};

  const params = new URLSearchParams({
    start: startDate,
    end: endDate,
    proxyAthlete: athleteId,
    isCheckIn: true,
  });
  const { response, data } = await fetchJson(
    `/actions/personal-records?${params}`
  );
  return response.ok ? data || {} : {};
};

// Mirrors calendar-month.controller.js's own contentType-gated, month-at-a-time
// loading (only the active tab fetches, never both) - but runs client-side via
// the no-reload week-plan bridge, and caches per month+tab in memory for the
// life of the page (no TTL/revalidation, same as phasesDescriptions elsewhere
// on this page) so paging the stepper within an already-seen month is instant.
export const useWeekPlanPreview = ({
  selectedWeek,
  athlete,
  athleteId,
  organizationCoachId,
  contentType,
  units,
  trackingItems,
}) => {
  const { t } = useTranslation("common");
  const cacheRef = useRef({});
  const [state, setState] = useState({
    data: null,
    isLoading: true,
    error: null,
  });

  const monthKey = moment(selectedWeek).format("YYYY-MM");
  const cacheKey = `${monthKey}:${contentType}`;

  useEffect(() => {
    if (cacheRef.current[cacheKey]) {
      setState({
        data: cacheRef.current[cacheKey],
        isLoading: false,
        error: null,
      });
      return;
    }

    let cancelled = false;
    setState({ data: null, isLoading: true, error: null });

    const weekStart = moment(selectedWeek).startOf("day");
    const monthStart = weekStart.clone().startOf("month");
    const weekEnd = weekStart.clone().add(6, "days");
    const monthEnd = moment.max(weekStart.clone().endOf("month"), weekEnd);
    const startDate = monthStart.format(DATE_DAY_FORMAT);
    const endDate = monthEnd.format(DATE_DAY_FORMAT);
    const params = new URLSearchParams({
      athleteId,
      contentType,
      startDate,
      endDate,
    });
    if (organizationCoachId)
      params.set("organizationCoachId", organizationCoachId);

    const primaryFetch = fetchJson(
      `/actions/annual-planner/json/week-plan?${params}`
    );
    // Log's secondary fetch has no dependency on the primary response, so it
    // starts at the same time as the primary fetch instead of after it -
    // Plan's (loadCycles) can't do the same, see that function's own comment.
    const logPersonalRecords =
      contentType === "log"
        ? loadDayPersonalRecords(athleteId, startDate, endDate)
        : null;

    primaryFetch
      .then(async ({ response, data }) => {
        if (!response.ok) throw new Error(data?.error || t("error"));
        if (contentType === "plan") {
          return { ...data, cycles: await loadCycles(data) };
        }
        return { ...data, dayPersonalRecords: await logPersonalRecords };
      })
      .then((data) => {
        if (cancelled) return;
        cacheRef.current[cacheKey] = data;
        setState({ data, isLoading: false, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ data: null, isLoading: false, error: e.message });
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, selectedWeek, athleteId, organizationCoachId, contentType, t]);

  const rowProps = useMemo(() => {
    if (!state.data) return null;
    return contentType === "log"
      ? buildLogRowProps(state.data, selectedWeek, units, athlete)
      : buildPlanRowProps(
          state.data,
          selectedWeek,
          monthKey,
          units,
          trackingItems
        );
  }, [
    state.data,
    selectedWeek,
    monthKey,
    contentType,
    units,
    athlete,
    trackingItems,
  ]);

  return { rowProps, isLoading: state.isLoading, error: state.error };
};
